import { roundMoney } from '@/utils/currency';
import { getItemPrice } from '@/utils/posItemPricing';
import type { ItemVariant, POSItem } from '@/types';

export type PromotionRuleType =
  | 'Percent Off'
  | 'Amount Off'
  | 'Special Price'
  | 'Quantity Break'
  | 'Buy X Get Y'
  | 'Cart Threshold';

export type PromotionScope = 'All' | 'Categories' | 'Items';
export type PromotionCustomerType = 'Retailer' | 'All' | 'Wholesale' | 'Distributor';
export type PromotionStatus = 'Paused' | 'Scheduled' | 'Active' | 'Ended';
export type PromotionOccasion = 'Custom' | 'Eid' | 'Christmas' | 'New Year' | 'Annual Sale' | 'Clearance';

export interface PromotionRule {
  name?: string;
  rule_name: string;
  is_active?: number | boolean;
  rule_type: PromotionRuleType;
  apply_scope: PromotionScope;
  discount_percent?: number;
  discount_amount?: number;
  special_price?: number;
  min_qty?: number;
  buy_qty?: number;
  get_qty?: number;
  get_discount_percent?: number;
  min_cart_amount?: number;
  cart_discount_percent?: number;
  cart_discount_amount?: number;
  priority?: number;
  categories?: string[];
  category_names?: string[];
  items?: string[];
  item_names?: string[];
  rule_categories?: { item_category: string; category_name?: string }[];
  rule_items?: { item: string; item_name?: string }[];
}

export interface PromotionCampaign {
  name?: string;
  campaign_name: string;
  is_active?: number | boolean;
  occasion?: PromotionOccasion;
  valid_from?: string | null;
  valid_to?: string | null;
  customer_types?: PromotionCustomerType;
  priority?: number;
  banner_text?: string;
  coupon_code?: string;
  notes?: string;
  status?: PromotionStatus;
  rule_count?: number;
  rules: PromotionRule[];
}

export interface PromoLineResult {
  index: number;
  original_rate: number;
  original_amount: number;
  charged_amount: number;
  promo_amount: number;
  promo_qty: number;
  promo_campaign?: string | null;
  promo_campaign_name?: string | null;
  promo_rule?: string | null;
  promo_rule_name?: string | null;
  promo_rule_type?: string | null;
  hint: string;
}

export interface PromoEvalResult {
  lines: PromoLineResult[];
  cart_promo: {
    promo_campaign: string;
    promo_campaign_name?: string;
    promo_rule: string;
    promo_rule_name?: string;
    saving: number;
  } | null;
  total_saving: number;
  original_subtotal: number;
  remaining_subtotal: number;
  campaigns: string[];
}

export interface ItemPromoPreview {
  badge: string;
  originalPrice: number;
  promoPrice?: number;
  strikethrough: boolean;
}

const LINE_TYPES: PromotionRuleType[] = [
  'Percent Off',
  'Amount Off',
  'Special Price',
  'Quantity Break',
  'Buy X Get Y',
];

function promoBadgeText(campaign: PromotionCampaign, fallback: string): string {
  const title = (campaign.campaign_name || '').trim();
  if (title) return title;
  const banner = (campaign.banner_text || '').trim();
  if (banner) return banner;
  return fallback;
}

function asBool(value: unknown, fallback = true): boolean {
  if (value === undefined || value === null) return fallback;
  return value === true || value === 1 || value === '1';
}

function parseWhen(value?: string | null): Date | null {
  if (!value) return null;
  const normalized = String(value).replace(' ', 'T');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function campaignStatus(
  campaign: Pick<PromotionCampaign, 'is_active' | 'valid_from' | 'valid_to'>,
  now = new Date(),
): PromotionStatus {
  if (!asBool(campaign.is_active, true)) return 'Paused';
  const from = parseWhen(campaign.valid_from);
  const to = parseWhen(campaign.valid_to);
  if (from && now < from) return 'Scheduled';
  if (to && now > to) return 'Ended';
  return 'Active';
}

function customerMatches(campaign: PromotionCampaign, customerType: string): boolean {
  const who = (campaign.customer_types || 'Retailer').trim() || 'Retailer';
  const current = (customerType || 'Retailer').trim() || 'Retailer';
  return who === 'All' || who === current;
}

function ruleItemIds(rule: PromotionRule): string[] {
  if (rule.items?.length) return rule.items;
  return (rule.rule_items || []).map((r) => r.item).filter(Boolean);
}

function ruleCategoryIds(rule: PromotionRule): string[] {
  const ids = [...(rule.categories || [])];
  const names = [...(rule.category_names || [])];
  for (const row of rule.rule_categories || []) {
    if (row.item_category) ids.push(row.item_category);
    if (row.category_name) names.push(row.category_name);
  }
  return [...ids, ...names];
}

function ruleMatchesItem(
  rule: PromotionRule,
  item: { item?: string; category?: string },
): boolean {
  const scope = rule.apply_scope || 'All';
  if (scope === 'All') return true;
  if (scope === 'Categories') {
    const category = item.category || '';
    return Boolean(category && ruleCategoryIds(rule).includes(category));
  }
  if (scope === 'Items') {
    const id = item.item || '';
    return Boolean(id && ruleItemIds(rule).includes(id));
  }
  return false;
}

function lineSaving(rule: PromotionRule, qty: number, rate: number): {
  saving: number;
  promoQty: number;
  hint: string;
} {
  const original = roundMoney(qty * rate);
  if (qty <= 0 || rate < 0 || original <= 0) return { saving: 0, promoQty: 0, hint: '' };

  if (rule.rule_type === 'Percent Off') {
    const pct = Number(rule.discount_percent) || 0;
    if (pct <= 0) return { saving: 0, promoQty: 0, hint: '' };
    return { saving: Math.min(roundMoney(original * pct / 100), original), promoQty: qty, hint: '' };
  }
  if (rule.rule_type === 'Amount Off') {
    const perUnit = Number(rule.discount_amount) || 0;
    if (perUnit <= 0) return { saving: 0, promoQty: 0, hint: '' };
    return { saving: roundMoney(Math.min(perUnit * qty, original)), promoQty: qty, hint: '' };
  }
  if (rule.rule_type === 'Special Price') {
    const special = Number(rule.special_price) || 0;
    if (special <= 0 || special >= rate) return { saving: 0, promoQty: 0, hint: '' };
    return { saving: Math.min(roundMoney((rate - special) * qty), original), promoQty: qty, hint: '' };
  }
  if (rule.rule_type === 'Quantity Break') {
    const minQty = Number(rule.min_qty) || 0;
    const pct = Number(rule.discount_percent) || 0;
    if (minQty <= 0 || pct <= 0) return { saving: 0, promoQty: 0, hint: '' };
    if (qty < minQty) {
      return { saving: 0, promoQty: 0, hint: `Add ${roundMoney(minQty - qty)} more for ${pct}% off` };
    }
    return { saving: Math.min(roundMoney(original * pct / 100), original), promoQty: qty, hint: '' };
  }
  if (rule.rule_type === 'Buy X Get Y') {
    const buyQty = Number(rule.buy_qty) || 2;
    const getQty = Number(rule.get_qty) || 1;
    const getPct = Number(rule.get_discount_percent) || 100;
    const cycle = buyQty + getQty;
    const freeLabel = getPct >= 100 ? 'free' : `${getPct}% off`;
    if (cycle <= 0 || qty < cycle) {
      const need = cycle - qty;
      return {
        saving: 0,
        promoQty: 0,
        hint: need > 0 ? `Add ${need} more to get ${getQty} ${freeLabel}` : '',
      };
    }
    const promoQty = Math.floor(qty / cycle) * getQty;
    const remainder = qty % cycle;
    const hint = remainder
      ? `Add ${cycle - remainder} more to get ${getQty} ${freeLabel}`
      : '';
    return {
      saving: Math.min(roundMoney(promoQty * rate * getPct / 100), original),
      promoQty,
      hint,
    };
  }
  return { saving: 0, promoQty: 0, hint: '' };
}

function cartSaving(rule: PromotionRule, remaining: number): number {
  if (remaining <= 0) return 0;
  const minAmount = Number(rule.min_cart_amount) || 0;
  if (remaining + 0.0001 < minAmount) return 0;
  const pct = Number(rule.cart_discount_percent) || 0;
  if (pct > 0) return roundMoney(Math.min(remaining * pct / 100, remaining));
  const amount = Number(rule.cart_discount_amount) || 0;
  if (amount > 0) return roundMoney(Math.min(amount, remaining));
  return 0;
}

export function evaluateCart(
  items: Array<{
    item?: string;
    category?: string;
    quantity: number;
    rate: number;
    amount?: number;
    variant?: string;
    minimum_price?: number;
  }>,
  customerType: string,
  promotions: PromotionCampaign[],
): PromoEvalResult {
  const empty: PromoEvalResult = {
    lines: [],
    cart_promo: null,
    total_saving: 0,
    original_subtotal: 0,
    remaining_subtotal: 0,
    campaigns: [],
  };
  if (!items.length) return empty;

  const eligible = (promotions || []).filter(
    (c) => asBool(c.is_active, true) && campaignStatus(c) === 'Active' && customerMatches(c, customerType),
  );
  type Enriched = PromotionRule & { _campaign: PromotionCampaign };
  const rules: Enriched[] = [];
  for (const campaign of eligible) {
    for (const rule of campaign.rules || []) {
      if (!asBool(rule.is_active, true)) continue;
      rules.push({ ...rule, _campaign: campaign });
    }
  }
  rules.sort((a, b) => {
    const cp = (b._campaign.priority || 100) - (a._campaign.priority || 100);
    if (cp) return cp;
    return (b.priority || 100) - (a.priority || 100);
  });

  const campaignsUsed = new Set<string>();
  const lines: PromoLineResult[] = items.map((item, idx) => {
    const qty = Number(item.quantity) || 0;
    const rate = Number(item.rate) || 0;
    const original = roundMoney(qty * rate);
    let best: { saving: number; promoQty: number; rule: Enriched; hint: string } | null = null;
    let hint = '';
    for (const rule of rules) {
      if (!LINE_TYPES.includes(rule.rule_type)) continue;
      if (!ruleMatchesItem(rule, item)) continue;
      const result = lineSaving(rule, qty, rate);
      if (result.hint && !hint) hint = result.hint;
      if (result.saving <= 0) continue;
      const candidate = { saving: result.saving, promoQty: result.promoQty, rule, hint: result.hint };
      if (!best || result.saving > best.saving + 0.0001) best = candidate;
      else if (Math.abs(result.saving - best.saving) <= 0.0001 && (rule.priority || 100) > (best.rule.priority || 100)) {
        best = candidate;
      }
    }
    let saving = best?.saving || 0;
    let charged = roundMoney(Math.max(0, original - saving));
    const minPrice = Number(item.minimum_price) || 0;
    if (minPrice > 0) {
      const floorAmount = roundMoney(minPrice * qty);
      if (charged < floorAmount) {
        charged = Math.min(original, floorAmount);
        saving = roundMoney(Math.max(0, original - charged));
        if (saving <= 0) best = null;
      }
    }
    if (best) {
      campaignsUsed.add(best.rule._campaign.campaign_name || best.rule._campaign.name || '');
      hint = best.hint || hint;
    }
    return {
      index: idx,
      original_rate: rate,
      original_amount: original,
      charged_amount: charged,
      promo_amount: saving,
      promo_qty: best?.promoQty || 0,
      promo_campaign: best?.rule._campaign.name,
      promo_campaign_name: best?.rule._campaign.campaign_name,
      promo_rule: best?.rule.name,
      promo_rule_name: best?.rule.rule_name,
      promo_rule_type: best?.rule.rule_type,
      hint,
    };
  });

  let remaining = roundMoney(lines.reduce((s, l) => s + l.charged_amount, 0));
  let cartBest: { saving: number; rule: Enriched } | null = null;
  for (const rule of rules) {
    if (rule.rule_type !== 'Cart Threshold') continue;
    const saving = cartSaving(rule, remaining);
    if (saving <= 0) continue;
    if (!cartBest || saving > cartBest.saving + 0.0001) cartBest = { saving, rule };
    else if (Math.abs(saving - cartBest.saving) <= 0.0001 && (rule.priority || 100) > (cartBest.rule.priority || 100)) {
      cartBest = { saving, rule };
    }
  }

  let cartPromo: PromoEvalResult['cart_promo'] = null;
  if (cartBest && remaining > 0) {
    const cartSave = Math.min(cartBest.saving, remaining);
    const eligibleIdxs = lines.map((l, i) => (l.charged_amount > 0 ? i : -1)).filter((i) => i >= 0);
    let distributed = 0;
    eligibleIdxs.forEach((idx, i) => {
      const line = lines[idx];
      const share = i === eligibleIdxs.length - 1
        ? roundMoney(cartSave - distributed)
        : roundMoney(cartSave * (line.charged_amount / remaining));
      const applied = Math.min(share, line.charged_amount);
      line.charged_amount = roundMoney(line.charged_amount - applied);
      line.promo_amount = roundMoney(line.promo_amount + applied);
      if (!line.promo_campaign) {
        line.promo_campaign = cartBest!.rule._campaign.name;
        line.promo_campaign_name = cartBest!.rule._campaign.campaign_name;
        line.promo_rule = cartBest!.rule.name;
        line.promo_rule_name = cartBest!.rule.rule_name;
        line.promo_rule_type = cartBest!.rule.rule_type;
      }
      distributed += applied;
    });
    campaignsUsed.add(cartBest.rule._campaign.campaign_name || cartBest.rule._campaign.name || '');
    cartPromo = {
      promo_campaign: cartBest.rule._campaign.name || '',
      promo_campaign_name: cartBest.rule._campaign.campaign_name,
      promo_rule: cartBest.rule.name || '',
      promo_rule_name: cartBest.rule.rule_name,
      saving: roundMoney(cartSave),
    };
  }

  remaining = roundMoney(lines.reduce((s, l) => s + l.charged_amount, 0));
  const originalSubtotal = roundMoney(lines.reduce((s, l) => s + l.original_amount, 0));
  return {
    lines,
    cart_promo: cartPromo,
    total_saving: roundMoney(originalSubtotal - remaining),
    original_subtotal: originalSubtotal,
    remaining_subtotal: remaining,
    campaigns: [...campaignsUsed].filter(Boolean).sort(),
  };
}

export function previewItemPromo(
  item: POSItem,
  customerType: string,
  promotions: PromotionCampaign[],
  variant?: ItemVariant,
): ItemPromoPreview | null {
  const price = getItemPrice(item, customerType, variant);
  if (price <= 0) return null;
  const eligible = (promotions || []).filter(
    (c) => asBool(c.is_active, true) && campaignStatus(c) === 'Active' && customerMatches(c, customerType),
  );
  let best: ItemPromoPreview | null = null;
  for (const campaign of eligible) {
    for (const rule of campaign.rules || []) {
      if (!asBool(rule.is_active, true)) continue;
      if (!LINE_TYPES.includes(rule.rule_type)) continue;
      if (!ruleMatchesItem(rule, { item: item.name, category: item.category })) continue;
      if (rule.rule_type === 'Percent Off' && (rule.discount_percent || 0) > 0) {
        const promoPrice = roundMoney(price * (1 - Number(rule.discount_percent) / 100));
        const candidate = {
          badge: promoBadgeText(campaign, `${Number(rule.discount_percent)}% off`),
          originalPrice: price,
          promoPrice,
          strikethrough: promoPrice < price,
        };
        if (!best || (candidate.promoPrice || price) < (best.promoPrice || best.originalPrice)) best = candidate;
      } else if (rule.rule_type === 'Amount Off' && (rule.discount_amount || 0) > 0) {
        const promoPrice = roundMoney(Math.max(0, price - Number(rule.discount_amount)));
        const candidate = {
          badge: promoBadgeText(campaign, 'Sale'),
          originalPrice: price,
          promoPrice,
          strikethrough: promoPrice < price,
        };
        if (!best || (candidate.promoPrice || price) < (best.promoPrice || best.originalPrice)) best = candidate;
      } else if (rule.rule_type === 'Special Price' && (rule.special_price || 0) > 0 && Number(rule.special_price) < price) {
        const candidate = {
          badge: promoBadgeText(campaign, 'Sale'),
          originalPrice: price,
          promoPrice: Number(rule.special_price),
          strikethrough: true,
        };
        if (!best || (candidate.promoPrice || price) < (best.promoPrice || best.originalPrice)) best = candidate;
      } else if (rule.rule_type === 'Quantity Break' && (rule.min_qty || 0) > 0) {
        if (!best) {
          best = {
            badge: promoBadgeText(
              campaign,
              `Buy ${Number(rule.min_qty)}+ : ${Number(rule.discount_percent)}% off`,
            ),
            originalPrice: price,
            strikethrough: false,
          };
        }
      } else if (rule.rule_type === 'Buy X Get Y') {
        if (!best) {
          const getPct = Number(rule.get_discount_percent) || 100;
          const generated = getPct >= 100
            ? `Buy ${rule.buy_qty || 2} get ${rule.get_qty || 1} free`
            : `Buy ${rule.buy_qty || 2} get ${rule.get_qty || 1} ${getPct}% off`;
          best = {
            badge: promoBadgeText(campaign, generated),
            originalPrice: price,
            strikethrough: false,
          };
        }
      }
    }
  }
  return best;
}

export function categoryNames(rule: PromotionRule): string[] {
  const fromRows = (rule.rule_categories || [])
    .map((r) => r.category_name || r.item_category)
    .filter(Boolean);
  if (fromRows.length) return fromRows;
  return (rule.category_names || []).filter(Boolean);
}

export function itemNames(rule: PromotionRule): string[] {
  const fromRows = (rule.rule_items || []).map((r) => r.item_name || r.item).filter(Boolean);
  if (fromRows.length) return fromRows;
  return (rule.item_names || []).filter(Boolean);
}

export function describeOffer(rule: PromotionRule): string {
  switch (rule.rule_type) {
    case 'Percent Off':
      return `${Number(rule.discount_percent) || 0}% off`;
    case 'Amount Off':
      return `${Number(rule.discount_amount) || 0} off each item`;
    case 'Special Price':
      return `Special price ${Number(rule.special_price) || 0}`;
    case 'Quantity Break':
      return `Buy ${Number(rule.min_qty) || 0}+ get ${Number(rule.discount_percent) || 0}% off`;
    case 'Buy X Get Y': {
      const getPct = Number(rule.get_discount_percent) || 100;
      return getPct >= 100
        ? `Buy ${rule.buy_qty || 2} get ${rule.get_qty || 1} free`
        : `Buy ${rule.buy_qty || 2} get ${rule.get_qty || 1} at ${getPct}% off`;
    }
    case 'Cart Threshold':
      if ((rule.cart_discount_percent || 0) > 0) {
        return `Spend ${Number(rule.min_cart_amount) || 0}, get ${Number(rule.cart_discount_percent)}% off the bill`;
      }
      return `Spend ${Number(rule.min_cart_amount) || 0}, get ${Number(rule.cart_discount_amount) || 0} off the bill`;
    default:
      return rule.rule_name;
  }
}

export function activeBanners(promotions: PromotionCampaign[], customerType: string): string[] {
  return (promotions || [])
    .filter((c) => asBool(c.is_active, true) && campaignStatus(c) === 'Active' && customerMatches(c, customerType))
    .map((c) => (c.banner_text || '').trim())
    .filter(Boolean);
}
